import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useCallback } from 'react'
import {
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import toast from 'react-hot-toast'

import FieldError from '@/components/field-error'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Platform,
  useCreateExternalApiKeyMutation,
} from '@/redux/api-key'

interface FormValues {
  key: string
  secret: string
}

export interface CreateExternalApiKeyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  platform: Platform
}

export default function CreateExternalApiKeyDialog({
  open,
  onOpenChange,
  platform: { id, name, logo },
}: CreateExternalApiKeyDialogProps) {
  const [createExternalApiKey] = useCreateExternalApiKeyMutation()
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>()
  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    await createExternalApiKey({ ...values, platformId: id }).unwrap()
    onOpenChange(false)
    reset()
    toast.success('API connect success.')
  }
  const onClose = useCallback(() => {
    onOpenChange(false)
    reset()
  }, [onOpenChange, reset])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showClose={false}>
        <VisuallyHidden>
          <DialogTitle>Connect {name} Account</DialogTitle>
        </VisuallyHidden>
        <div className="flex items-center gap-3 mb-1">
          <img className="size-10 p-2 bg-white/4 rounded-full" src={logo} alt={name} />
          <div className="text-xl font-semibold">Connect {name} Account</div>
        </div>
        <div className="text-tertiary-foreground text-sm">
          Add your {name} API key to connect your account
        </div>
        <form className="py-8 grid gap-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <Label required className="mb-3" htmlFor="key">API Key</Label>
            <Input
              {...register(
                'key',
                {
                  required: 'API Key cannot be empty',
                },
              )}
              id="key"
              aria-invalid={!!errors.key}
            />
            <FieldError error={errors.key} />
          </div>
          <div>
            <Label required className="mb-3" htmlFor="secret">API Secret</Label>
            <Input
              {...register(
                'secret',
                {
                  required: 'API Secret cannot be empty',
                },
              )}
              id="secret"
              aria-invalid={!!errors.secret}
            />
            <FieldError error={errors.secret} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button isLoading={isSubmitting} type="submit" disabled={isSubmitting}>
              Connect
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
